import { useState, useEffect, useRef, useCallback } from 'react';
import io from 'socket.io-client';
import API_URL from '../config/API_URL';

export const useRooms = (roomId, username) => {
  const [socket, setSocket] = useState(null);
  const [isInRoom, setIsInRoom] = useState(false);
  const [roomUsers, setRoomUsers] = useState([]);
  const [remoteStreams, setRemoteStreams] = useState({});
  const [connectionStatus, setConnectionStatus] = useState('Disconnected');

  const [isUsingScreenShare, setIsUsingScreenShare] = useState(false);

  const localVideoRef = useRef(null);
  const localStreamRef = useRef(null);
  const peerConnectionsRef = useRef({});
  const socketRef = useRef(null); // ⭐ ADD THIS - store socket in ref too

  const [servers, setServers] = useState({
    iceServers: [
      { urls: 'stun:stun.l.google.com:19302' }
    ]
  });

  // Get Twilio TURN servers
  useEffect(() => {
    const getTurnServers = async () => {
      try {
        const response = await fetch(`${API_URL}/api/turn-credentials`, {
          method: 'POST',
          credentials: 'include'
        });
        const data = await response.json();
        console.log('🎯 Got TURN servers:', data.iceServers);
        setServers({ iceServers: data.iceServers });
      } catch (error) {
        console.error('❌ Failed to get TURN servers:', error);
      }
    };
    getTurnServers();
  }, []);

  const shouldCall = (myId, theirId) => myId < theirId;

  const createPeerConnection = useCallback((remoteUserId) => {
    console.log(`🔗 Creating peer connection for user: ${remoteUserId}`);
    
    const pc = new RTCPeerConnection(servers);
    peerConnectionsRef.current[remoteUserId] = pc;

    // Add local stream
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => {
        console.log(`➕ Adding ${track.kind} track for ${remoteUserId}`);
        pc.addTrack(track, localStreamRef.current);
      });
    }

    // Handle remote stream
    pc.ontrack = (event) => {
      console.log(`🎉 Received remote stream from ${remoteUserId}`);
      setRemoteStreams(prev => ({
        ...prev,
        [remoteUserId]: event.streams[0]
      }));
    };

    pc.onicecandidate = (event) => {
      if (event.candidate && socketRef.current) {
        console.log(`🧊 Sending ICE candidate to ${remoteUserId}`);
        socketRef.current.emit('webrtc-message', {
          type: 'ice-candidate',
          candidate: event.candidate,
          roomId: roomId,
          targetUser: remoteUserId
        });
      }
    };

    pc.onconnectionstatechange = () => {
      console.log(`Connection with ${remoteUserId}:`, pc.connectionState);
    };
    
    pc.oniceconnectionstatechange = () => {
      console.log(`🧊 ICE connection with ${remoteUserId}:`, pc.iceConnectionState);
    };

    return pc;
  }, [servers, roomId]);

  const initiateCallToUser = useCallback(async (targetUserId) => {
    console.log(`📞 Initiating call to ${targetUserId}`);
    console.log(`   Socket exists: ${!!socketRef.current}`);
    console.log(`   Socket connected: ${socketRef.current?.connected}`);
    console.log(`   Socket ID: ${socketRef.current?.id}`);
    
    if (!socketRef.current) {
      console.log('❌ Socket not initialized, retrying...');
      setTimeout(() => initiateCallToUser(targetUserId), 1000);
      return;
    }
    
    if (peerConnectionsRef.current[targetUserId]) {
      console.log(`⏭️ Already have peer connection for ${targetUserId}`);
      return;
    }

    try {
      console.log(`✅ Creating peer connection for ${targetUserId}`);
      const pc = createPeerConnection(targetUserId);
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      
      console.log(`📤 Emitting offer to ${targetUserId}`);
      socketRef.current.emit('webrtc-message', {
        type: 'offer',
        offer: offer,
        roomId: roomId,
        targetUser: targetUserId
      });
      console.log(`✅ Offer sent successfully to ${targetUserId}`);
    } catch (error) {
      console.error('❌ Error initiating call:', error);
    }
  }, [roomId, createPeerConnection]);

  const handleWebRTCMessage = useCallback(async (data) => {
    console.log(`📨 Handling ${data.type} from ${data.from}`);
    const { type, from } = data;

    try {
      if (type === 'offer') {
        const pc = createPeerConnection(from);
        await pc.setRemoteDescription(new RTCSessionDescription(data.offer));
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        
        if (socketRef.current) {
          socketRef.current.emit('webrtc-message', {
            type: 'answer',
            answer: answer,
            roomId: roomId,
            targetUser: from
          });
        }
        
      } else if (type === 'answer') {
        const pc = peerConnectionsRef.current[from];
        if (pc) {
          await pc.setRemoteDescription(new RTCSessionDescription(data.answer));
        }
        
      } else if (type === 'ice-candidate') {
        const pc = peerConnectionsRef.current[from];
        if (pc && data.candidate) {
          await pc.addIceCandidate(new RTCIceCandidate(data.candidate));
        }
      }
    } catch (error) {
      console.error(`❌ Error handling ${type}:`, error);
    }
  }, [roomId, createPeerConnection]);

  const getMediaStream = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { width: 1280, height: 720 }, 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });
      return stream;
    }  catch (error) {
      console.error('❌ Media error:', error);
      setConnectionStatus('Media Error');
      
      // ⭐ CREATE BLACK SCREEN CANVAS INSTEAD OF FAILING
      console.log('📹 Creating black video placeholder...');
      
      const canvas = document.createElement('canvas');
      canvas.width = 640;
      canvas.height = 480;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = 'black';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Add text to black screen
      ctx.fillStyle = 'white';
      ctx.font = '24px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('Camera Unavailable', canvas.width / 2, canvas.height / 2);
      
      const blackStream = canvas.captureStream(30);
      
      // Add silent audio track
      const audioCtx = new AudioContext();
      const oscillator = audioCtx.createOscillator();
      const dst = oscillator.connect(audioCtx.createMediaStreamDestination());
      oscillator.start();
      const audioTrack = dst.stream.getAudioTracks()[0];
      blackStream.addTrack(audioTrack);
      
      setConnectionStatus('Connected (No Camera)');
      
      // ⭐ RETURN THE BLACK STREAM INSTEAD OF THROWING
      return blackStream;
    }
  };

  const forceReconnectToPeer = async (targetUserId) => {
    console.log(`🔄 FORCING RECONNECT to ${targetUserId}`);
    
    if (peerConnectionsRef.current[targetUserId]) {
      peerConnectionsRef.current[targetUserId].close();
      delete peerConnectionsRef.current[targetUserId];
    }
    
    setRemoteStreams(prev => {
      const updated = { ...prev };
      delete updated[targetUserId];
      return updated;
    });
    
    setTimeout(() => {
      if (localStreamRef.current && socketRef.current && shouldCall(socketRef.current.id, targetUserId)) {
        initiateCallToUser(targetUserId);
      }
    }, 1000);
  };

  useEffect(() => {
    if (!roomId) return;

    let isMounted = true; // ⭐ ADD THIS LINE

    console.log('🚀 Initializing VideoChat for room:', roomId);
    
    const newSocket = io(`${API_URL}`, {
      withCredentials: true,
      transports: ['websocket', 'polling']
    });
    
    setSocket(newSocket);
    socketRef.current = newSocket;

    // Get media stream - ONLY if still mounted
    getMediaStream()
      .then(stream => {
        if (!isMounted) {  // ⭐ ADD THIS CHECK
          console.log('⚠️ Component unmounted, stopping tracks');
          stream.getTracks().forEach(track => track.stop());
          return;
        }
        
        localStreamRef.current = stream;
        if (localVideoRef.current) {
          localVideoRef.current.srcObject = stream;
        }
        setConnectionStatus('Ready');
      })
      .catch(error => {
        console.error('❌ Media error:', error);
        console.log('🖥️ Camera failed, trying screen share...');
        
        // Try screen share instead
        navigator.mediaDevices.getDisplayMedia({ 
          video: true, 
          audio: true 
        })
          .then(stream => {
            if (!isMounted) {
              stream.getTracks().forEach(track => track.stop());
              return;
            }
            
            localStreamRef.current = stream;
            if (localVideoRef.current) {
              localVideoRef.current.srcObject = stream;
            }
            setConnectionStatus('Screen Share');
            setIsUsingScreenShare(true);
          })
          .catch(screenError => {
            console.error('❌ Screen share also failed:', screenError);
            setConnectionStatus('Media Error');
          });
      });


    // Socket listeners
    newSocket.on('connect', () => {
      console.log('🔌 Connected to server');
      socketRef.current = newSocket; // Update ref on connect
      setConnectionStatus('Connected');
    });

    newSocket.on('disconnect', () => {
      console.log('🔌 Disconnected');
      setConnectionStatus('Disconnected');
      setIsInRoom(false);
    });

    newSocket.on('room-info', (data) => {
      console.log('🏠 Room info:', data);
      console.log('   My socket ID:', newSocket.id);
      console.log('   Other users:', data.users);
      
      // ⭐ ADD YOUR OWN ID TO THE LIST
      const allUsers = data.users.includes(newSocket.id) 
        ? data.users 
        : [...data.users, newSocket.id];
      
      console.log('   All users in room:', allUsers);
      setRoomUsers(allUsers);
      setIsInRoom(true);
    });

    newSocket.on('user-joined', (data) => {
      console.log('👋 User joined:', data.userId);
      setRoomUsers(prev => {
        const updated = [...prev.filter(id => id !== data.userId), data.userId];
        return updated;
      });
      
      // ⭐ CRITICAL: When someone NEW joins, establish connection
      // Wait a bit for them to get their local stream ready
      setTimeout(() => {
        if (localStreamRef.current && newSocket.id && shouldCall(newSocket.id, data.userId)) {
          console.log(`🆕 New user joined, initiating call to ${data.userId}`);
          initiateCallToUser(data.userId);
        }
      }, 1000);
    });

    newSocket.on('user-left', (data) => {
      console.log('👋 User left:', data.userId);
      setRoomUsers(prev => prev.filter(id => id !== data.userId));
      
      if (peerConnectionsRef.current[data.userId]) {
        peerConnectionsRef.current[data.userId].close();
        delete peerConnectionsRef.current[data.userId];
      }
      
      setRemoteStreams(prev => {
        const newStreams = { ...prev };
        delete newStreams[data.userId];
        return newStreams;
      });
    });

    newSocket.on('webrtc-message', handleWebRTCMessage);

    return () => {
    isMounted = false; // ⭐ ADD THIS LINE
    console.log('🧹 Cleanup');
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
    }
    Object.values(peerConnectionsRef.current).forEach(pc => pc.close());
    newSocket.disconnect();
    socketRef.current = null;
  };
}, [roomId]);


  const startScreenShare = async () => {
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({ 
        video: true, 
        audio: true 
      });
      
      // Stop current video track before replacing
      if (localStreamRef.current) {
        const currentVideoTrack = localStreamRef.current.getVideoTracks()[0];
        if (currentVideoTrack) {
          currentVideoTrack.stop();
        }
      }
      
      // Keep existing audio track
      const existingAudioTrack = localStreamRef.current?.getAudioTracks()[0];
      const screenVideoTrack = stream.getVideoTracks()[0];
      
      // Create new stream with screen video and existing audio
      const newStream = new MediaStream([
        screenVideoTrack,
        ...(existingAudioTrack ? [existingAudioTrack] : [])
      ]);
      
      localStreamRef.current = newStream;
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = newStream;
      }
      
      // Replace video track in all peer connections
      Object.values(peerConnectionsRef.current).forEach(pc => {
        const senders = pc.getSenders();
        const sender = senders.find(s => s.track && s.track.kind === 'video');
        if (sender) {
          sender.replaceTrack(screenVideoTrack);
        }
      });
      
      // Handle when user stops sharing via browser UI
      screenVideoTrack.onended = () => {
        console.log('📺 Screen share ended by user');
        // You may want to trigger returning to camera here
      };
      
      return true;
    } catch (error) {
      console.error('❌ Screen share failed:', error);
      return false;
    }
  };

  const joinRoom = useCallback(() => {
    if (socketRef.current && roomId) {
      console.log(`🚪 Joining room: ${roomId}`);
      socketRef.current.emit('join-room', { roomId: roomId, username: username });
    } else {
      console.log('❌ Cannot join room - socket not ready');
    }
  }, [roomId, username]);

  const callAllUsers = useCallback(() => {
    console.log('📞 Calling all users in room');
    console.log('   My socket ID:', socketRef.current?.id);
    console.log('   Room users:', roomUsers);
    console.log('   Socket exists:', !!socketRef.current);
    
    if (!socketRef.current || !socketRef.current.id) {
      console.log('❌ Socket not available in callAllUsers');
      return;
    }
    
    roomUsers.forEach(userId => {
      if (userId !== socketRef.current.id) {
        console.log(`   Checking user ${userId}:`);
        console.log(`     - Already have stream: ${!!remoteStreams[userId]}`);
        console.log(`     - Should call: ${shouldCall(socketRef.current.id, userId)}`);
        
        if (!remoteStreams[userId] && shouldCall(socketRef.current.id, userId)) {
          console.log(`   ✅ Will call ${userId}`);
          setTimeout(() => initiateCallToUser(userId), 500);
        } else {
          console.log(`   ⏭️ Skipping ${userId}`);
        }
      }
    });
  }, [roomUsers, remoteStreams, initiateCallToUser]);

  return {
    socket,
    isInRoom,
    roomUsers,
    remoteStreams,
    connectionStatus,
    localVideoRef,
    localStreamRef,
    peerConnectionsRef,
    forceReconnectToPeer,
    joinRoom,
    startScreenShare,
    callAllUsers,
    localStream: localStreamRef.current,
    hasLocalStream: !!localStreamRef.current,
    remoteStreamCount: Object.keys(remoteStreams).length,
    isUsingScreenShare
  };
};